/**
 * Admin → Roles: which role may do what (roles are a setting). Custom roles have a base type —
 * Office (Google sign-in, office screens) or Mechanic (PIN on the iPad, own jobs, never prices).
 * The server refuses anything that would lock out the Admin or give a mechanic prices.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { BaseType, Capability, Me, RoleDefs } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { can } from '../../lib/perm';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, PageHeader, Select } from '../../components/ui';

interface RolesResponse {
  roles: RoleDefs;
  builtIn: string[];
  capabilities: { key: Capability; group: string; label: string }[];
  mechanicCapabilities: Capability[];
  adminLocked: Capability[];
  inUse: Record<string, number>;
}

export function RolesPage({ me }: { me: Me }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['roles'], queryFn: () => api.get<RolesResponse>('/api/roles') });
  const [draft, setDraft] = useState<RoleDefs | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const editable = can(me, 'settings.manage');
  const roles = draft ?? q.data?.roles ?? {};
  const keys = useMemo(() => {
    const builtIn = q.data?.builtIn ?? [];
    return [...builtIn, ...Object.keys(roles).filter((k) => !builtIn.includes(k)).sort()];
  }, [q.data, roles]);

  if (q.isLoading || !q.data) return <Loading />;
  const d = q.data;
  const groups = [...new Set(d.capabilities.map((c) => c.group))];

  const allowed = (key: string, cap: Capability) => {
    if (!editable) return false;
    if (roles[key]?.base === 'MECHANIC' && !d.mechanicCapabilities.includes(cap)) return false;
    if (roles[key]?.base === 'OFFICE' && cap === 'time.shop') return false;
    if (key === 'ADMIN' && d.adminLocked.includes(cap)) return false;
    return true;
  };
  const toggle = (key: string, cap: Capability) => {
    const r = roles[key]!;
    const caps = r.caps.includes(cap) ? r.caps.filter((c) => c !== cap) : [...r.caps, cap];
    setDraft({ ...roles, [key]: { ...r, caps } });
    setSaved(false);
  };
  const save = async () => {
    setError(null);
    try {
      await api.put('/api/roles', roles);
      setDraft(null);
      setSaved(true);
      await qc.invalidateQueries({ queryKey: ['roles'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
    } catch (e) {
      setError(errorText(e));
    }
  };
  const remove = (key: string) => {
    setDraft(Object.fromEntries(Object.entries(roles).filter(([k]) => k !== key)));
  };

  return (
    <>
      <PageHeader title="Roles">
        {editable && <Button variant="secondary" onClick={() => setAdding(true)}>Add role</Button>}
        {editable && <Button onClick={save} disabled={!draft}>Save changes</Button>}
      </PageHeader>
      <p className="mb-3 text-sm text-slate-600">
        Tick what each role may do. Changes take effect on the next click for everyone with that role. Admin always keeps user and settings management; Mechanic roles never see prices or office screens; nobody can approve their own void; notes, time history and the audit log can never be edited or deleted.
      </p>
      <ErrorText error={error} />
      {saved && <p className="mb-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-800">Saved.</p>}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-200 text-xs text-slate-600">
              <tr>
                <th className="px-2 py-2">What they can do</th>
                {keys.map((k) => (
                  <th key={k} className="px-2 py-2 text-center">
                    <div className="font-semibold text-slate-800">{roles[k]?.label}</div>
                    <div>{roles[k]?.base === 'MECHANIC' ? 'iPad (PIN)' : 'Office (Google)'} · {d.inUse[k] ?? 0} user(s)</div>
                    {editable && !d.builtIn.includes(k) && !(d.inUse[k] ?? 0) && <button className="text-red-700 hover:underline" onClick={() => remove(k)}>Remove</button>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows key={g} group={g} caps={d.capabilities.filter((c) => c.group === g)} keys={keys} roles={roles} allowed={allowed} toggle={toggle} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {adding && <AddRole existing={Object.keys(roles)} onClose={() => setAdding(false)} onAdd={(key, label, base) => { setDraft({ ...roles, [key]: { label, base, caps: [] } }); setAdding(false); }} />}
    </>
  );
}

function GroupRows({ group, caps, keys, roles, allowed, toggle }: { group: string; caps: RolesResponse['capabilities']; keys: string[]; roles: RoleDefs; allowed: (k: string, c: Capability) => boolean; toggle: (k: string, c: Capability) => void }) {
  return (
    <>
      <tr className="bg-slate-50"><td colSpan={keys.length + 1} className="px-2 py-1 text-xs font-bold uppercase text-slate-500">{group}</td></tr>
      {caps.map((c) => (
        <tr key={c.key} className="border-b border-slate-100">
          <td className="px-2 py-1.5">{c.label}</td>
          {keys.map((k) => (
            <td key={k} className="px-2 py-1.5 text-center">
              <input type="checkbox" className="h-4 w-4" checked={roles[k]?.caps.includes(c.key) ?? false} disabled={!allowed(k, c.key)} onChange={() => toggle(k, c.key)} aria-label={`${roles[k]?.label}: ${c.label}`} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

function AddRole({ existing, onClose, onAdd }: { existing: string[]; onClose: () => void; onAdd: (key: string, label: string, base: BaseType) => void }) {
  const [label, setLabel] = useState('');
  const [base, setBase] = useState<BaseType>('OFFICE');
  const key = label.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  const valid = /^[A-Z][A-Z0-9_]{1,39}$/.test(key) && !existing.includes(key);
  return (
    <Modal title="Add role" onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button disabled={!valid} onClick={() => onAdd(key, label.trim(), base)}>Add</Button></>}>
      <Field label="Name" required hint={key ? `Key: ${key}${existing.includes(key) ? ' (already used)' : ''}` : undefined}>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus placeholder="e.g. Payroll" />
      </Field>
      <Field label="Type" hint="Decides how they sign in and which screens they get. Can't be changed once someone has the role.">
        <Select value={base} onChange={(e) => setBase(e.target.value as BaseType)}>
          <option value="OFFICE">Office — Google sign-in, office screens</option>
          <option value="MECHANIC">Mechanic — PIN on the iPad, own jobs only, no prices</option>
        </Select>
      </Field>
      <p className="text-sm text-slate-600">Then tick what the role may do and press Save changes.</p>
    </Modal>
  );
}
